import * as Sentry from '@sentry/react';
import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  teacherUnitsCapabilities,
  type UnitsApiAdapter,
  type UnitsModule,
} from '@ludolio/shared-units';
import { apiClient } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';

const teacherUnitsApi: UnitsApiAdapter = {
  listUnits: () => apiClient.getMyUnits(),
  createUnit: (data) => apiClient.createUnit(data),
  updateUnit: (unitId, data) => apiClient.updateUnit(unitId, data),
  getUnitById: (unitId) => apiClient.getUnitById(unitId),
  getUnitStudents: (unitId) => apiClient.getUnitStudents(unitId),
  getUnitGames: (unitId) => apiClient.getUnitGames(unitId),
  getUnitTeachers: (unitId) => apiClient.getUnitTeachers(unitId),
  listActivities: (unitId) => apiClient.listActivities(unitId),
  addStudentsByEmail: (unitId, emails) => apiClient.addStudentsByEmail(unitId, emails),
  getInvitationBatchProgress: (batchId) => apiClient.getInvitationBatchProgress(batchId),
  removePendingEnrollment: (unitId, pendingId) => apiClient.removePendingEnrollment(unitId, pendingId),
  resendStudentInvitation: (unitId, pendingId) => apiClient.resendStudentInvitation(unitId, pendingId),
  removeStudentFromUnit: (unitId, studentId) => apiClient.removeStudentFromUnit(unitId, studentId),
  addTeachersByEmail: (unitId, emails) => apiClient.addTeachersByEmail(unitId, emails),
  removeTeacherFromUnit: (unitId, userId) => apiClient.removeTeacherFromUnit(unitId, userId),
  removePendingUnitTeacher: (unitId, pendingId) => apiClient.removePendingUnitTeacher(unitId, pendingId),
  resendPendingUnitTeacherInvitation: (unitId, pendingId) => apiClient.resendPendingUnitTeacherInvitation(unitId, pendingId),
  getAllGames: () => apiClient.getAllGames(),
  addGameToUnit: (unitId, gameId) => apiClient.addGameToUnit(unitId, gameId),
  removeGameFromUnit: (unitId, gameId) => apiClient.removeGameFromUnit(unitId, gameId),
  getGameAchievements: (gameId) => apiClient.getGameAchievements(gameId),
  createActivity: (data) => apiClient.createActivity(data),
  updateActivity: (data) => apiClient.updateActivity(data),
  deleteActivity: (activityId) => apiClient.deleteActivity(activityId),
  getActivityResponses: (activityId) => apiClient.getActivityResponses(activityId),
  listActivityLibrary: () => apiClient.listActivityLibrary(),
  importActivityToUnit: (libraryItemId, unitId) => apiClient.importActivityToUnit(libraryItemId, unitId),
  createActivityLibraryItem: (data) => apiClient.createActivityLibraryItem(data),
  deleteUnit: (unitId, confirmationCode) => apiClient.deleteUnit(unitId, confirmationCode),
};

export function useTeacherUnitsModule(): UnitsModule {
  const navigate = useNavigate();
  const currentUser = useAuthStore((state) => state.user);

  return useMemo(() => ({
    api: teacherUnitsApi,
    capabilities: teacherUnitsCapabilities,
    currentUser,
    reportError: Sentry.captureException,
    navigateToUnits: () => navigate({ to: '/units' }),
    navigateToUnit: (unitId: string) => navigate({ to: '/units/$unitId', params: { unitId } }),
    getRegion: (unitRegion?: string | null) => unitRegion,
  }), [currentUser, navigate]);
}
